import React, { useEffect, useState } from 'react';

import { differenceInMinutes, format, parse } from 'date-fns';
import dayjs, { Dayjs } from 'dayjs';

import EditIcon from '@mui/icons-material/Edit';
import {
  Alert,
  Box,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { StaticDatePicker } from '@mui/x-date-pickers/StaticDatePicker';

import Button from 'components/Button';
import { apiMain } from 'services/apiService';
import { useIsMobile } from 'utils/mediaQueries';

import 'dayjs/locale/pt-br';

dayjs.locale('pt-br');

interface SchedulingProps {
  requesterId: string;
  hostId: string;
  onClose: () => void;
  hostName?: string;
}

interface AvailableSlot {
  start_time: string;
  end_time: string;
  availability_slot_id: string;
}

const REASONS = [
  { value: 'CONNECTION', label: 'Conexão' },
  { value: 'NETWORKING', label: 'Networking' },
  { value: 'BUSINESS', label: 'Negócios' },
];

const Scheduling: React.FC<SchedulingProps> = ({ requesterId, hostId, onClose, hostName = '' }) => {
  const isMobile = useIsMobile();
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(null);
  const [availableSlots, setAvailableSlots] = useState<AvailableSlot[]>([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState<AvailableSlot | null>(null);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [reason, setReason] = useState<string | null>(null);
  const [message, setMessage] = useState<string>('');

  useEffect(() => {
    if (!selectedDate) return;
    setLoadingSlots(true);
    setAvailableSlots([]);
    setSelectedSlot(null);
    setError(null);
    apiMain
      .get(`/meetings/v1/availability-slots/users/${hostId}/available-slots`, {
        params: { date: selectedDate.format('YYYY-MM-DD') },
      })
      .then(res => {
        setAvailableSlots(res.data || []);
      })
      .catch(() => {
        setError('Erro ao buscar horários disponíveis para o dia selecionado.');
      })
      .finally(() => setLoadingSlots(false));
  }, [selectedDate, hostId]);

  const handleReset = () => {
    setSelectedDate(null);
    setSelectedSlot(null);
    // mantém reason e message
  };

  const canSubmit =
    !!selectedDate && !!selectedSlot && !!reason && message.length > 0 && !submitting;

  const handleSubmit = async () => {
    if (!selectedDate || !selectedSlot || !reason || !message) return;
    setSubmitting(true);
    setError(null);
    try {
      const meetingDate = selectedDate.format('YYYY-MM-DD');
      const startTime = `${selectedSlot.start_time}:00.000Z`;
      const duration = differenceInMinutes(
        parse(selectedSlot.end_time, 'HH:mm', new Date()),
        parse(selectedSlot.start_time, 'HH:mm', new Date())
      );
      const requestBody = {
        requester_id: requesterId,
        host_id: hostId,
        availability_slot_id: selectedSlot.availability_slot_id,
        meeting_date: meetingDate,
        start_time: startTime,
        duration_minutes: duration,
        reason,
        message,
      };
      console.log('[Scheduling] Body que será enviado:', requestBody);
      await apiMain.post('/meetings/v1/meeting-requests', requestBody);
      setSuccess(true);
    } catch (err: any) {
      setError('Erro ao marcar horário.');
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
        <Box
          display="flex"
          flexDirection="column"
          alignItems="start"
          justifyContent="center"
          height="fit-content"
        >
          <Typography mb={8} alignSelf="left">
            Solicitação de agendamento enviado!
          </Typography>
          <Button variant="outline" color="primary" onClick={onClose} fullWidth>
            Fechar
          </Button>
        </Box>
      </LocalizationProvider>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
      <Box
        sx={{
          height: '70vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Cabeçalho com reset e data/horário selecionados */}
        {(selectedSlot || selectedDate) && (
          <Box display="flex" alignItems="center" justifyContent="center" mb={2}>
            <Typography variant="h6" fontWeight={700} color="text.primary" mr={1}>
              {selectedDate && selectedSlot
                ? `${selectedDate.format('DD/MM/YYYY')} - ${selectedSlot.start_time}h`
                : selectedDate
                  ? `${selectedDate.format('DD/MM/YYYY')}`
                  : ''}
            </Typography>
            <IconButton size="small" onClick={handleReset} aria-label="Resetar seleção">
              <EditIcon fontSize="small" sx={{ color: 'primary.main' }} />
            </IconButton>
          </Box>
        )}
        {/* Conteúdo principal com scroll */}
        <Box
          sx={{
            flex: 1,
            overflow: 'auto',
            p: isMobile ? 2 : 0,
            pb: 0,
          }}
        >
          <Stack spacing={3}>
            <Typography variant="body2" align="left">
              Escolha um horário disponível para conectar com <b>{hostName}</b>
            </Typography>
            {error && <Alert severity="error">{error}</Alert>}
            {/* Fluxo: calendário -> horários -> motivo/mensagem */}
            {!selectedDate && (
              <Box
                display="flex"
                justifyContent="center"
                sx={{
                  width: '100%',
                  border: '1px solid',
                  borderColor: 'text.primary',
                  borderRadius: 0,
                  overflow: 'hidden',
                  '& .MuiStaticDatePicker-root': {
                    width: isMobile ? '100%' : 'auto',
                  },
                  '& .MuiPickersLayout-root': {
                    border: 'none',
                  },
                }}
              >
                <StaticDatePicker
                  displayStaticWrapperAs="desktop"
                  value={selectedDate}
                  onChange={setSelectedDate}
                  disablePast
                  localeText={{
                    toolbarTitle: 'Selecione uma data',
                    previousMonth: 'Mês anterior',
                    nextMonth: 'Próximo mês',
                    cancelButtonLabel: 'Cancelar',
                    okButtonLabel: 'OK',
                    todayButtonLabel: 'Hoje',
                  }}
                />
              </Box>
            )}
            {selectedDate && !selectedSlot && (
              <Box>
                {loadingSlots ? (
                  <Box display="flex" justifyContent="center" alignItems="center" minHeight={80}>
                    <CircularProgress />
                  </Box>
                ) : availableSlots.length === 0 ? (
                  <Typography color="text.secondary" align="center" variant="body1" mb={2}>
                    Nenhum horário disponível para este dia.
                  </Typography>
                ) : (
                  <Box
                    sx={{
                      maxHeight: '300px',
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      p: 1,
                    }}
                  >
                    <Stack spacing={1} alignItems="center" sx={{ width: '100%' }}>
                      {availableSlots.map(slot => (
                        <Box
                          key={slot.availability_slot_id + slot.start_time}
                          onClick={() => setSelectedSlot(slot)}
                          sx={{
                            width: '100%',
                            maxWidth: isMobile ? '100%' : '400px',
                            p: 1.2,
                            border: '1px solid',
                            borderColor: selectedSlot === slot ? 'primary.main' : 'text.primary',
                            borderRadius: 0,
                            cursor: 'pointer',
                            transition: 'all 0.2s ease-in-out',
                            backgroundColor:
                              selectedSlot === slot ? 'primary.light' + '10' : 'transparent',
                            '&:hover': {
                              borderColor: 'primary.main',
                              backgroundColor: 'primary.light' + '05',
                            },
                          }}
                        >
                          <Typography
                            variant="body1"
                            align="center"
                            sx={{
                              color: selectedSlot === slot ? 'primary.main' : 'text.primary',
                              fontWeight: selectedSlot === slot ? 600 : 400,
                            }}
                          >
                            {format(parse(slot.start_time, 'HH:mm', new Date()), 'HH:mm')} -{' '}
                            {format(parse(slot.end_time, 'HH:mm', new Date()), 'HH:mm')}
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                  </Box>
                )}
              </Box>
            )}
            {selectedSlot && (
              <Stack spacing={2}>
                <FormControl fullWidth>
                  <InputLabel id="reason-label">Motivo</InputLabel>
                  <Select
                    labelId="reason-label"
                    value={reason || ''}
                    onChange={e => setReason(e.target.value)}
                    label="Motivo"
                  >
                    {REASONS.map(opt => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  label="Mensagem"
                  multiline
                  rows={4}
                  value={message}
                  onChange={e => setMessage(e.target.value.slice(0, 500))}
                  helperText={`${message.length}/500`}
                  fullWidth
                />
              </Stack>
            )}
          </Stack>
        </Box>
        <Box
          sx={{
            borderTop: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
            p: isMobile ? 2 : 0,
            pt: 2,
          }}
        >
          <Stack direction={isMobile ? 'column' : 'row'} spacing={2}>
            <Button variant="outline" color="inherit" onClick={onClose} fullWidth>
              Cancelar
            </Button>
            <Button
              variant="primary"
              color="primary"
              onClick={handleSubmit}
              fullWidth
              disabled={!canSubmit}
            >
              {submitting ? <CircularProgress size={22} /> : 'Marcar horário'}
            </Button>
          </Stack>
        </Box>
      </Box>
    </LocalizationProvider>
  );
};

export default Scheduling;
